import {
  bondingCurveRealQuoteReserveD6,
  deriveBondingCurvePoolOutcomeReserveD6,
} from './bonding-curve.js';
import type { MarketPoolQuoteSnapshot } from './snapshot-context.js';

export interface MarketPoolQuoteSnapshotRow {
  readonly pubkey: string;
  readonly status: 'created' | 'baking' | 'trading' | 'resolved' | 'graduated';
  /** Base64 market-state cursor; null means the market has no indexed event yet. */
  readonly marketStateSequence: string | null;
  readonly winnerOutcomeIndex: number | null;
  readonly convictionUpdatedAt?: string;
  readonly outcomes: readonly {
    readonly outcomeIndex: number;
    readonly mint: string;
    readonly graduated: boolean;
    readonly convictionAnchor?: string;
    readonly currentPoolState:
      | {
          readonly virtualQuoteReserveD6: string;
          readonly virtualInclusiveQuoteReserveD6: string;
          readonly outstandingOutcomeSupplyD6: string;
        }
      | {
          readonly quoteVaultReserveD6: string;
          readonly outcomeVaultReserveD6: string;
          readonly tradeFeeRateMillionths?: string;
          readonly creatorFeeRateMillionths?: string;
          readonly swapEnabled?: boolean;
          readonly openTimeUnixSeconds?: string;
          readonly observedAtUnixSeconds?: string;
          readonly creatorFeeEnabled?: boolean;
          readonly creatorFeeOn?: 'both' | 'token0' | 'token1';
          readonly outcomeTokenIndex?: 0 | 1;
        };
  }[];
}

export interface MarketPoolQuoteSnapshotConfig {
  readonly resolutionMode?: 'conviction' | 'timed';
  readonly tradingStartedUnixSeconds?: string;
  readonly tradingEndUnixSeconds?: string;
  readonly feeBpsBuy?: number;
  readonly feeBpsSellNormal?: number;
  readonly feeBpsSellPenaltyExtra?: number;
  readonly feeBpsPrizePool?: number;
  readonly convictionResolutionThresholdPpb?: string;
  readonly convictionRetentionPerSecondPpb?: string;
  readonly initialTokenSupply?: string;
  readonly minQuoteInPerTrade?: string;
  readonly convictionBaselineReserveQuote?: string;
  readonly convictionReferenceMarginPpb?: string;
}

/** Projects indexed market reserve fields into the quote-only math contract. */
export function marketPoolQuoteSnapshotFromRow(
  row: MarketPoolQuoteSnapshotRow,
  config: MarketPoolQuoteSnapshotConfig,
): MarketPoolQuoteSnapshot {
  const lifecycle = row.status === 'baking' ? 'backing' : row.status;
  const chainSlot = chainSlotFromMarketStateSequence(row.marketStateSequence);
  if (lifecycle === 'graduated') return graduatedSnapshot(row, chainSlot);
  const time = requiredString(row.convictionUpdatedAt, 'convictionUpdatedAt');
  if (lifecycle !== 'trading') {
    return terminalSnapshot(row, lifecycle, chainSlot, time);
  }
  return tradingSnapshot(row, config, chainSlot, time);
}

function graduatedSnapshot(
  row: MarketPoolQuoteSnapshotRow,
  chainSlot: string,
): MarketPoolQuoteSnapshot {
  const graduatedOutcome = row.outcomes.find((outcome) => outcome.graduated);
  if (graduatedOutcome === undefined) throw new Error('graduated market has no cp-swap outcome');
  const observedState = requiredCpSwapPoolState(graduatedOutcome.currentPoolState);
  const time = requiredString(observedState.observedAtUnixSeconds, 'observedAtUnixSeconds');
  return {
    market: row.pubkey,
    chainSlot,
    chainUnixTimestampSeconds: time,
    lifecycle: 'graduated',
    winningOutcomeIndex: row.winnerOutcomeIndex,
    bondingCurvesClosed: true,
    buyFeeBps: null,
    sellBaseFeeBps: null,
    sellPenaltyFeeBps: null,
    prizePoolFeeBps: null,
    minimumQuoteTradeD6: null,
    bondingCurveTradeGate: null,
    outcomes: row.outcomes.map((outcome) => {
      if (!outcome.graduated) {
        return {
          outcomeIndex: outcome.outcomeIndex,
          outcomeMint: outcome.mint,
          realQuoteReserveD6: null,
          bondingCurve: null,
          cpSwap: null,
        };
      }
      const state = requiredCpSwapPoolState(outcome.currentPoolState);
      const tradeFeeRateMillionths = requiredString(
        state.tradeFeeRateMillionths,
        'tradeFeeRateMillionths',
      );
      const creatorFeeRateMillionths = requiredString(
        state.creatorFeeRateMillionths,
        'creatorFeeRateMillionths',
      );
      return {
        outcomeIndex: outcome.outcomeIndex,
        outcomeMint: outcome.mint,
        realQuoteReserveD6: unsigned(state.quoteVaultReserveD6, 'quoteVaultReserveD6'),
        bondingCurve: null,
        cpSwap: {
          quoteReserveD6: unsigned(state.quoteVaultReserveD6, 'quoteVaultReserveD6'),
          outcomeReserveD6: unsigned(state.outcomeVaultReserveD6, 'outcomeVaultReserveD6'),
          tradeFeeRateMillionths,
          creatorFeeRateMillionths,
          creatorFeeEnabled: requiredBoolean(state.creatorFeeEnabled, 'creatorFeeEnabled'),
          creatorFeeOn: requiredCreatorFeeOn(state.creatorFeeOn),
          outcomeTokenIndex: requiredTokenIndex(state.outcomeTokenIndex),
          swapEnabled: requiredBoolean(state.swapEnabled, 'swapEnabled'),
          openTimeUnixSeconds: requiredString(state.openTimeUnixSeconds, 'openTimeUnixSeconds'),
        },
      };
    }),
  };
}

function terminalSnapshot(
  row: MarketPoolQuoteSnapshotRow,
  lifecycle: Exclude<MarketPoolQuoteSnapshot['lifecycle'], 'trading' | 'graduated'>,
  chainSlot: string,
  time: string,
): MarketPoolQuoteSnapshot {
  return {
    market: row.pubkey,
    chainSlot,
    chainUnixTimestampSeconds: time,
    lifecycle,
    winningOutcomeIndex: row.winnerOutcomeIndex,
    bondingCurvesClosed: true,
    buyFeeBps: null,
    sellBaseFeeBps: null,
    sellPenaltyFeeBps: null,
    prizePoolFeeBps: null,
    minimumQuoteTradeD6: null,
    bondingCurveTradeGate: null,
    outcomes: row.outcomes.map((outcome) => ({
      outcomeIndex: outcome.outcomeIndex,
      outcomeMint: outcome.mint,
      realQuoteReserveD6: null,
      bondingCurve: null,
      cpSwap: null,
    })),
  };
}

function tradingSnapshot(
  row: MarketPoolQuoteSnapshotRow,
  config: MarketPoolQuoteSnapshotConfig,
  chainSlot: string,
  time: string,
): MarketPoolQuoteSnapshot {
  const initialSupply = requiredString(config.initialTokenSupply, 'initialTokenSupply');
  const outcomes = row.outcomes.map((outcome) => {
    if (outcome.graduated) {
      throw new Error('trading market has a non-bonding-curve pool state');
    }
    const poolState = requiredBondingCurvePoolState(outcome.currentPoolState);
    const virtualQuote = unsigned(poolState.virtualQuoteReserveD6, 'virtualQuoteReserveD6');
    const poolQuote = unsigned(
      poolState.virtualInclusiveQuoteReserveD6,
      'virtualInclusiveQuoteReserveD6',
    );
    const sold = unsigned(poolState.outstandingOutcomeSupplyD6, 'outstandingOutcomeSupplyD6');
    const poolSupply = deriveBondingCurvePoolOutcomeReserveD6(BigInt(initialSupply), BigInt(sold));
    if (!poolSupply.available) throw new Error('invalid bonding curve supply reserve');
    const realQuote = bondingCurveRealQuoteReserveD6({
      virtualQuoteReserveD6: BigInt(virtualQuote),
      poolQuoteReserveD6: BigInt(poolQuote),
      initialOutcomeSupplyD6: BigInt(initialSupply),
      poolOutcomeReserveD6: poolSupply.value,
    });
    if (!realQuote.available) throw new Error('invalid bonding curve quote reserve');
    return {
      outcome,
      virtualQuote,
      poolQuote,
      sold,
      poolSupply: poolSupply.value,
      realQuote: realQuote.value,
    };
  });
  return {
    market: row.pubkey,
    chainSlot,
    chainUnixTimestampSeconds: time,
    lifecycle: 'trading',
    winningOutcomeIndex: null,
    bondingCurvesClosed: false,
    buyFeeBps: requiredNumber(config.feeBpsBuy, 'feeBpsBuy'),
    sellBaseFeeBps: requiredNumber(config.feeBpsSellNormal, 'feeBpsSellNormal'),
    sellPenaltyFeeBps: requiredNumber(config.feeBpsSellPenaltyExtra, 'feeBpsSellPenaltyExtra'),
    prizePoolFeeBps: requiredNumber(config.feeBpsPrizePool, 'feeBpsPrizePool'),
    minimumQuoteTradeD6: requiredString(config.minQuoteInPerTrade, 'minQuoteInPerTrade'),
    bondingCurveTradeGate: {
      ...(config.resolutionMode === 'timed'
        ? {
            timedTradingWindow: {
              startUnixSeconds: requiredString(
                config.tradingStartedUnixSeconds,
                'tradingStartedUnixSeconds',
              ),
              endUnixSeconds: requiredString(config.tradingEndUnixSeconds, 'tradingEndUnixSeconds'),
            },
          }
        : {}),
      outcomeConvictionPpb: row.outcomes.map((outcome) =>
        requiredString(outcome.convictionAnchor, 'convictionAnchor'),
      ),
      lastConvictionUpdateUnixSeconds: time,
      convictionBaselineReserveQuoteD6: requiredString(
        config.convictionBaselineReserveQuote,
        'convictionBaselineReserveQuote',
      ),
      convictionRetentionPerSecondPpb: unsigned(
        requiredString(config.convictionRetentionPerSecondPpb, 'convictionRetentionPerSecondPpb'),
        'convictionRetentionPerSecondPpb',
      ),
      convictionResolutionThresholdPpb: unsigned(
        config.resolutionMode === 'timed'
          ? '0'
          : requiredString(
              config.convictionResolutionThresholdPpb,
              'convictionResolutionThresholdPpb',
            ),
        'convictionResolutionThresholdPpb',
      ),
      convictionReferenceMarginPpb: requiredString(
        config.convictionReferenceMarginPpb,
        'convictionReferenceMarginPpb',
      ),
    },
    outcomes: outcomes.map(({ outcome, virtualQuote, poolQuote, poolSupply, realQuote }) => ({
      outcomeIndex: outcome.outcomeIndex,
      outcomeMint: outcome.mint,
      realQuoteReserveD6: realQuote.toString(),
      bondingCurve: {
        virtualQuoteReserveD6: virtualQuote,
        poolQuoteReserveD6: poolQuote,
        initialOutcomeSupplyD6: initialSupply,
        poolOutcomeReserveD6: poolSupply.toString(),
        closedForTrade: false,
      },
      cpSwap: null,
    })),
  };
}

function requiredBondingCurvePoolState(
  state: MarketPoolQuoteSnapshotRow['outcomes'][number]['currentPoolState'],
): Extract<
  MarketPoolQuoteSnapshotRow['outcomes'][number]['currentPoolState'],
  { virtualQuoteReserveD6: string }
> {
  if (!('virtualQuoteReserveD6' in state)) {
    throw new Error('trading market has a non-bonding-curve pool state');
  }
  return state;
}

function requiredCpSwapPoolState(
  state: MarketPoolQuoteSnapshotRow['outcomes'][number]['currentPoolState'],
): Extract<
  MarketPoolQuoteSnapshotRow['outcomes'][number]['currentPoolState'],
  { quoteVaultReserveD6: string }
> {
  if (!('quoteVaultReserveD6' in state)) {
    throw new Error('graduated market has a non-cp-swap pool state');
  }
  return state;
}

function requiredBoolean(value: boolean | undefined, name: string): boolean {
  if (typeof value !== 'boolean') throw new Error(`missing or invalid ${name}`);
  return value;
}

function requiredCreatorFeeOn(
  value: 'both' | 'token0' | 'token1' | undefined,
): 'both' | 'token0' | 'token1' {
  if (value === 'both' || value === 'token0' || value === 'token1') return value;
  throw new Error('missing or invalid creatorFeeOn');
}

function requiredTokenIndex(value: 0 | 1 | undefined): 0 | 1 {
  if (value === 0 || value === 1) return value;
  throw new Error('missing or invalid outcomeTokenIndex');
}

function requiredString(value: string | undefined, name: string): string {
  return unsigned(value ?? '', name);
}

function requiredNumber(value: number | undefined, name: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`missing or invalid ${name}`);
  }
  return value;
}

function unsigned(value: string, name: string): string {
  if (!/^(?:0|[1-9]\d*)$/.test(value)) throw new Error(`missing or invalid ${name}`);
  return value;
}

function chainSlotFromMarketStateSequence(sequence: string | null): string {
  if (sequence === null) return '0';
  const cursor = base64Cursor(sequence);
  if (cursor === null || cursor.length !== 15 || (cursor[0] ?? 0) > 15) {
    throw new Error('missing or invalid marketStateSequence');
  }
  let packed = 0n;
  for (const byte of cursor) packed = (packed << 8n) | BigInt(byte);
  return ((packed >> 76n) & ((1n << 40n) - 1n)).toString();
}

function base64Cursor(value: string): Uint8Array | null {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const clean = value.replace(/=+$/, '');
  if (clean.length === 0) return null;
  const bytes = new Uint8Array((clean.length * 3) >> 2);
  let accumulator = 0;
  let bits = 0;
  let index = 0;
  for (const character of clean) {
    const digit = alphabet.indexOf(character);
    if (digit < 0) return null;
    accumulator = (accumulator << 6) | digit;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[index] = (accumulator >> bits) & 0xff;
      index += 1;
    }
  }
  return bytes;
}
